import { FormEvent, useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { ApiError } from '../../common/Rest/ReaderlyHttpClient';
import { createShopInquiry } from '../../common/Rest/Services/ShopService';
import { useAuth } from '../../common/Auth/AuthProvider';

const MAX_MESSAGE_LENGTH = 1000;

const isValidPhone = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return /^\+?[\d\s\-()]+$/.test(value.trim()) && digits.length >= 9 && digits.length <= 15;
};

interface ShopInquiryDialogProps {
  open: boolean;
  itemId: number;
  itemName: string;
  onClose: () => void;
}

function ShopInquiryDialog({ open, itemId, itemName, onClose }: ShopInquiryDialogProps) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSent(false);
    setSubmitError(null);
    setErrors({});
    setName((current) => current || user?.name || '');
  }, [open, user?.name]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors: { name?: string; phone?: string } = {};
    if (!name.trim()) nextErrors.name = "Вкажіть, як до вас звертатися.";
    if (!isValidPhone(phone)) nextErrors.phone = 'Введіть телефон, наприклад +380671234567.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      await createShopInquiry(itemId, { name: name.trim(), phone: phone.trim(), message: message.trim() || undefined });
      setSent(true);
      setMessage('');
    } catch (err) {
      setSubmitError(err instanceof ApiError && err.status === 429
        ? 'Забагато запитів. Спробуйте трохи пізніше або напишіть нам в Instagram.'
        : 'Не вдалося надіслати запит. Спробуйте ще раз.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      {sent ? (
        <>
          <DialogTitle>Дякуємо!</DialogTitle>
          <DialogContent>
            <p>Ми отримали ваш запит щодо «{itemName}» і зателефонуємо або напишемо найближчим часом.</p>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={onClose}>Закрити</Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <DialogTitle>Уточнити деталі</DialogTitle>
          <DialogContent>
            <p className="shop-inquiry-hint">
              Хочете інший напис, колір чи розмір для «{itemName}»? Залиште номер — ми зв'яжемося з вами.
            </p>
            <TextField
              label="Ім'я"
              value={name}
              onChange={(event) => setName(event.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              required
              fullWidth
              margin="dense"
            />
            <TextField
              label="Телефон"
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              error={!!errors.phone}
              helperText={errors.phone || 'Подзвонимо або напишемо в Viber/Telegram'}
              slotProps={{ htmlInput: { maxLength: 20, autoComplete: 'tel' } }}
              required
              fullWidth
              margin="dense"
            />
            <TextField
              label="Що хочете уточнити?"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              slotProps={{ htmlInput: { maxLength: MAX_MESSAGE_LENGTH } }}
              placeholder="Наприклад: напис «Читаю, не заважати» білими літерами"
              multiline
              minRows={3}
              fullWidth
              margin="dense"
            />
            {submitError && <Alert severity="error">{submitError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={submitting}>Скасувати</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Надсилаємо...' : 'Надіслати'}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}

export default ShopInquiryDialog;
